import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import Modal from '../components/Modal';

interface Color {
  _id: string;
  name: string;
  hexCode?: string;
  firmCode?: string;
  tcxCode?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

const ColorPool: React.FC = () => {
  const [colors, setColors] = useState<Color[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [colorToDelete, setColorToDelete] = useState<Color | null>(null);
  const { hasPermission, isAdmin } = useContext(AuthContext);

  useEffect(() => {
    fetchColors();
  }, []);

  const fetchColors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/colors');
      setColors(response.data);
      setError(null);
    } catch (err) {
      setError('Renkler yüklenirken bir hata oluştu');
      console.error('Error fetching colors:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (color: Color) => {
    setColorToDelete(color);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!colorToDelete) return;
    
    try {
      await api.delete(`/colors/${colorToDelete._id}`);
      setColors(colors.filter(c => c._id !== colorToDelete._id));
      setDeleteModalOpen(false);
      setColorToDelete(null);
    } catch (err) {
      setError('Renk silinirken bir hata oluştu');
      console.error('Error deleting color:', err);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setColorToDelete(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Renk Havuzu</h1>
        {/* Renk ekleme butonu - sadece izni olanlar görebilir */}
        {(isAdmin || hasPermission('colors', 'create')) && (
          <Link 
            to="/colors/create" 
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            + Yeni Renk Ekle
          </Link>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
              <th className="py-3 px-6 text-left">Renk</th>
              <th className="py-3 px-6 text-left">Ad</th>
              <th className="py-3 px-6 text-left">Firma Kodu</th>
              <th className="py-3 px-6 text-left">TCX Kodu</th>
              <th className="py-3 px-6 text-left">Hex Kodu</th>
              <th className="py-3 px-6 text-left">Notlar</th>
              <th className="py-3 px-6 text-center">İşlemler</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 text-sm">
            {colors.map((color) => (
              <tr key={color._id} className="border-b border-gray-200 hover:bg-gray-50">
                <td className="py-3 px-6">
                  <div 
                    className="w-8 h-8 rounded-full border border-gray-300"
                    style={{ backgroundColor: color.hexCode ? `#${color.hexCode}` : 'lightgray' }}
                    title={color.name}
                  />
                </td>
                <td className="py-3 px-6">{color.name}</td>
                <td className="py-3 px-6">{color.firmCode || '-'}</td>
                <td className="py-3 px-6">{color.tcxCode || '-'}</td>
                <td className="py-3 px-6">{color.hexCode || '-'}</td>
                <td className="py-3 px-6">{color.notes || '-'}</td>
                <td className="py-3 px-6 text-center">
                  <div className="flex item-center justify-center space-x-4">
                    {/* Düzenleme butonu - sadece update izni olanlar görebilir */}
                    {(isAdmin || hasPermission('colors', 'update')) && (
                      <Link 
                        to={`/colors/${color._id}/edit`} 
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        <span>Düzenle</span>
                      </Link>
                    )}
                    
                    {/* Silme butonu - sadece delete izni olanlar görebilir */}
                    {(isAdmin || hasPermission('colors', 'delete')) && (
                      <button
                        onClick={() => handleDeleteClick(color)}
                        className="text-red-600 hover:text-red-900 flex items-center"
                      >
                        <span>Sil</span>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {colors.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-gray-500">
                  Henüz renk eklenmemiş
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={deleteModalOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Rengi Sil"
        type="error"
      >
        <p>
          <span className="font-bold">{colorToDelete?.name}</span> adlı rengi silmek istediğinizden emin misiniz?
          <br />
          Bu işlem geri alınamaz.
        </p>
      </Modal>
    </div>
  );
};

export default ColorPool;